/**
 * Advisory Request System Types
 *
 * Type definitions for the simplified advisory flow
 * Based on /market/v1/lead-request API endpoints
 */

import { UserData } from './userData';

export enum LeadRequestStatus {
  NEW = 'NEW',
  CANCELED = 'CANCELED',
  REQUESTED = 'REQUESTED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

export enum InvitationLinkStatus {
  NEW = 'NEW',
  PENDING = 'PENDING',
  USED = 'USED',
  EXPIRED = 'EXPIRED',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
}

export interface LeadRequest {
  id: string;
  requester_user_id: string;
  lead_user_id?: string | null;
  organization_uuid: string;
  status: LeadRequestStatus | string;
  form_responses?: Record<string, any> | any[] | null;
  created_at: string;
  updated_at: string;
  invitation_links?: InvitationLink[];
}

export interface InviteData {
  id: string;
  invitee_first_name: string;
  invitee_last_name: string;
  invitee_cell_phone: string;
  invitee_email: string;
  invite_status: string;
  invited_at: string;
  type: string;
  extras?: {
    lead_request_id: string;
    [key: string]: any;
  };
  user_id: string;
  updated_at: string;
}

export interface InvitationLink {
  id: string;
  link: string;
  lead_request_id: string;
  created_at: string;
  expiration_date: string;
  status: InvitationLinkStatus | string;
  lead_user_id?: string | null;
  lead_user_data?: UserData | null;
  invite_data?: InviteData | null;
}

export interface CreateLeadRequestDTO {
  requester_user_id: string;
  lead_user_id?: string | null;
  form_responses?: Record<string, any> | any[] | null;
}

export interface UpdateLeadRequestDTO {
  status?: LeadRequestStatus | string;
  lead_user_id?: string | null;
  form_responses?: Record<string, any> | any[] | null;
}

export interface CreateInvitationLinkDTO {
  lead_request_id: string;
  link: string;
  invite_id?: string;
}

export interface UpdateInvitationLinkDTO {
  status?: InvitationLinkStatus | string;
  lead_user_id?: string | null;
  invite_id?: string;
}

export interface LeadRequestsResponse {
  items: LeadRequest[];
  total: number;
  page: number;
  size: number;
  pages?: number;
}

export interface LeadRequestFilters {
  requester_user_id?: string;
  lead_user_id?: string;
  page?: number;
  size?: number;
}

/**
 * Enriched LeadRequest with user data
 * Includes complete user information for requester and lead users
 */
export interface LeadRequestWithUserData extends LeadRequest {
  requester_user_data?: UserData | null;
}

/**
 * Enriched LeadRequestsResponse with user data
 */
export interface LeadRequestsWithUserDataResponse {
  items: LeadRequestWithUserData[];
  total: number;
  page: number;
  size: number;
  pages?: number;
}
